import InstrumentPicker, { chipCls } from "@/components/creator/music/InstrumentPicker";

const MAX_INSTRUMENTS = 8;
const UPLOAD_NOTE = {
  add_band: "Your singing sets the tempo, key and length.",
  add_singing: "Your music sets the tempo, key, length and instruments.",
  grow_loop: "Your loop sets the tempo and key. The song grows to fit your lyrics — add instruments to hear them join in.",
};

function Row({ label, hint, children }) {
  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-white/70">{label}</p>
      {hint ? <p className="mt-0.5 text-[11px] text-white/40">{hint}</p> : null}
      <div className="mt-2">{children}</div>
    </div>
  );
}

/** Tempo · key · structure · length · instruments (only instruments when a band goes under your uploaded singing). */
export default function MusicControls({ catalog, form, genre, onChange, mode = "scratch" }) {
  const exactLength = form.vocal?.mode === "instrumental" || (form.lyrics || "").trim().length >= 20 || form.lyrics_lock;
  const instruments = (
    <Row label="Instruments" hint={`Pick up to ${MAX_INSTRUMENTS} — leave empty to let the genre decide`}>
      <InstrumentPicker catalog={catalog} value={form.instruments} max={MAX_INSTRUMENTS} onChange={(next) => onChange({ instruments: next })} />
    </Row>
  );
  if (mode !== "scratch") {
    return (
      <div data-testid="music-controls" className="space-y-6">
        <p data-testid="music-controls-upload-note" className="rounded-2xl border border-white/[0.08] bg-black/30 px-3.5 py-3 text-[12px] leading-relaxed text-white/60">{UPLOAD_NOTE[mode]}</p>
        {mode === "add_band" || mode === "grow_loop" ? instruments : null}
      </div>
    );
  }
  return (
    <div data-testid="music-controls" className="space-y-6">
      <Row label="Tempo" hint={form.bpm ? `${form.bpm} BPM` : genre?.bpm ? `Auto — ${genre.label} usually sits near ${genre.bpm} BPM` : "Auto — let the music decide"}>
        <div className="flex items-center gap-3">
          <input data-testid="adv-bpm" type="range" min={50} max={200} step={1} value={form.bpm || genre?.bpm || 90} onChange={(e) => onChange({ bpm: Number(e.target.value) })} className="studio-fader w-full" />
          <button type="button" data-testid="adv-bpm-auto" aria-pressed={!form.bpm} onClick={() => onChange({ bpm: null })} className={chipCls(!form.bpm)}>Auto</button>
        </div>
      </Row>
      <div className="grid gap-4">
        <Row label="Musical key">
          <select data-testid="adv-key" value={form.key || ""} onChange={(e) => onChange({ key: e.target.value || null })} className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-blue-400/60 focus:outline-none">
            <option value="">Auto</option>
            {catalog.keys.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </Row>
        <Row label="Song structure">
          <select data-testid="structure-select" value={form.structure} onChange={(e) => onChange({ structure: e.target.value })} className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-blue-400/60 focus:outline-none">
            {catalog.structures.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </Row>
      </div>
      <Row label="Duration" hint={exactLength ? "Song length — 3 to 5 minutes" : "Exact length applies when you use your own lyrics"}>
        <div className="flex flex-wrap gap-2">
          {catalog.lengths.map((l) => (
            <button key={l.label} type="button" data-testid={`length-${l.secs}`} aria-pressed={form.duration_sec === l.secs} onClick={() => onChange({ duration_sec: l.secs })} className={chipCls(form.duration_sec === l.secs)}>
              {l.label}
            </button>
          ))}
        </div>
      </Row>
      {instruments}
    </div>
  );
}
